// P&ID-nahe SVG-Symbolbibliothek für das Anlagenschema (Blackfort-CI).
//
// Line-art, ruhiger Ingenieurs-Look: Gold-/Weiß-Strich auf Navy, angelehnt an ISA-5.1 und
// DIN EN ISO 10628, bewusst schlicht und lesbar gehalten. Jede Symboldefinition rendert genau ein
// erkennbares Glyph; ein Glyph ist eine reine <svg>-Zeichnung (keine Farb-/Größenlogik — die kommt
// vom Aufrufer über className/Attribute), damit dasselbe Icon im Knoten (groß) und in der Palette
// (klein) verwendbar ist.
//
// Das Mapping ist datengetrieben: `glyphFor(symbolId, category)` schlägt zuerst die Symbol-ID im
// SYMBOL_GLYPHS-Register nach; fehlt ein exakter Eintrag, greift ein Kategorie-Fallback
// (behaelter/maschine/sensor/aktor_sollwert). So bekommt jedes real vorkommende Symbol ein
// passendes Icon, und neue Katalog-Symbole erhalten ohne Frontend-Änderung wenigstens das
// Kategorie-Icon. Wird im Katalog später ein `glyph`-Feld ergänzt, kann der Aufrufer es bevorzugt
// an `glyphFor` durchreichen (rückwärtskompatibel).

import type { JSX, SVGProps } from 'react';

// Gemeinsame Zeichen-Konventionen. Der Strich erbt die CI-Farbe über `currentColor`, damit der
// Aufrufer die Farbe (Gold für Kontur, Weiß für Akzente) per Tailwind-Textfarbe steuern kann.
const STROKE = 'currentColor';
const SW = 1.6; // Grundstrichstärke — dünn genug für ruhige Line-art, dick genug zum Lesen.

// Ein Glyph ist eine Funktion, die den Inhalt eines 40×40-viewBox-SVG liefert. Der Wrapper (Svg)
// setzt viewBox, Fülllosigkeit und die Standard-Strichattribute; die Glyphen zeichnen nur Geometrie.
type Glyph = () => JSX.Element;

function Svg({ children, ...rest }: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <svg
      viewBox="0 0 40 40"
      fill="none"
      stroke={STROKE}
      strokeWidth={SW}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      {children}
    </svg>
  );
}

// ── Behälter / Becken ─────────────────────────────────────────────────────────────────────

// Fermenter / Hauptgärer / Nachgärer: stehender Rundbehälter mit Kuppel und Rührwerk-Welle.
const gFermenter: Glyph = () => (
  <>
    <path d="M8 16 A12 8 0 0 1 32 16" />
    <line x1="8" y1="16" x2="8" y2="32" />
    <line x1="32" y1="16" x2="32" y2="32" />
    <path d="M8 32 A12 4 0 0 0 32 32" />
    {/* Rührwerk-Welle mit Blatt */}
    <line x1="20" y1="9" x2="20" y2="27" />
    <line x1="15" y1="27" x2="25" y2="27" />
  </>
);

// Faulturm / Faulbehälter (Klärschlamm): Ei-/Zylinderform mit spitzer Kuppel und Domschacht.
const gFaulturm: Glyph = () => (
  <>
    <path d="M10 18 A10 10 0 0 1 30 18" />
    <line x1="10" y1="18" x2="10" y2="30" />
    <line x1="30" y1="18" x2="30" y2="30" />
    <path d="M10 30 A10 5 0 0 0 30 30" />
    {/* Domschacht auf der Kuppel */}
    <rect x="17" y="6" width="6" height="4" />
    <line x1="20" y1="10" x2="20" y2="14" />
  </>
);

// Gasspeicher / Gashaube / Tragluftdach: Kuppel/Ballon über Grundlinie (gespeichertes Gasvolumen).
const gGasspeicher: Glyph = () => (
  <>
    <path d="M7 30 A13 13 0 0 1 33 30" />
    <line x1="6" y1="30" x2="34" y2="30" />
    <line x1="20" y1="30" x2="20" y2="17" strokeDasharray="2 2" />
  </>
);

// Rundklärer (Vor-/Nachklärung): Rundbecken mit mittiger Räumerbrücke und Räumerarm.
const gRundklaerer: Glyph = () => (
  <>
    <circle cx="20" cy="20" r="13" />
    <circle cx="20" cy="20" r="2" />
    {/* Räumerbrücke über den Durchmesser + abgesetzter Räumerarm */}
    <line x1="7" y1="20" x2="33" y2="20" />
    <line x1="20" y1="20" x2="27" y2="27" />
  </>
);

// Belebungsbecken: Becken mit aufsteigenden Belüftungsblasen (Sauerstoffeintrag).
const gBelebung: Glyph = () => (
  <>
    <path d="M7 12 L7 30 L33 30 L33 12" />
    {/* Belüftungsblasen */}
    <circle cx="14" cy="24" r="1.6" />
    <circle cx="14" cy="19" r="1.6" />
    <circle cx="20" cy="26" r="1.6" />
    <circle cx="20" cy="21" r="1.6" />
    <circle cx="26" cy="24" r="1.6" />
    <circle cx="26" cy="19" r="1.6" />
  </>
);

// Sandfang (belüftet): länglicher Behälter mit Bodenrinne und Belüftungsblasen.
const gSandfang: Glyph = () => (
  <>
    <path d="M7 13 L7 27 L33 27 L33 13" />
    {/* Sandrinne am Boden */}
    <path d="M15 27 L20 32 L25 27" />
    <circle cx="13" cy="20" r="1.4" />
    <circle cx="20" cy="18" r="1.4" />
    <circle cx="27" cy="20" r="1.4" />
  </>
);

// Zulauf / Zulaufbauwerk: Gerinne mit einlaufendem Pfeil (Zufluss von links).
const gZulauf: Glyph = () => (
  <>
    <path d="M6 14 L34 14 M6 26 L34 26" />
    {/* Strömungspfeil nach rechts */}
    <line x1="12" y1="20" x2="28" y2="20" />
    <path d="M24 16 L28 20 L24 24" />
  </>
);

// Ablauf / Ablaufbauwerk: Gerinne mit auslaufendem Pfeil und Überfallkante.
const gAblauf: Glyph = () => (
  <>
    <path d="M6 14 L34 14 M6 26 L34 26" />
    <line x1="12" y1="20" x2="30" y2="20" />
    <path d="M26 16 L30 20 L26 24" />
    {/* Überfallkante */}
    <line x1="18" y1="14" x2="18" y2="26" />
  </>
);

// Eindicker / Voreindicker: Trichterbecken (konischer Boden) — Schlamm sinkt ab.
const gEindicker: Glyph = () => (
  <>
    <path d="M8 12 L32 12" />
    <path d="M8 12 L20 30 L32 12" />
    {/* Schlammabzug am Trichterboden */}
    <line x1="20" y1="30" x2="20" y2="34" />
  </>
);

// Kondensatabscheider / Gaskühler-Kondensat: liegender Behälter mit Kondensat-Sumpf und Ablauf.
const gKondensat: Glyph = () => (
  <>
    <rect x="8" y="12" width="24" height="14" rx="7" />
    {/* Kondensat-Sumpf mit Tropfen-Ablauf */}
    <line x1="20" y1="26" x2="20" y2="31" />
    <path d="M18 31 L20 34 L22 31 Z" />
  </>
);

// ── Maschinen / Aktoren ───────────────────────────────────────────────────────────────────

// Pumpe / Pumpwerk (Kreiselpumpe): Kreis mit eingeschriebenem Dreieck (Förderrichtung), ISA-üblich.
const gPumpe: Glyph = () => (
  <>
    <circle cx="20" cy="20" r="11" />
    <path d="M14 14 L28 20 L14 26 Z" />
  </>
);

// Exzenterschneckenpumpe: Pumpenkreis mit angedeuteter Schnecken-Wellenlinie.
const gSchneckenpumpe: Glyph = () => (
  <>
    <circle cx="20" cy="20" r="11" />
    <path d="M13 20 q3.5 -5 7 0 q3.5 5 7 0" />
  </>
);

// Dosierpumpe / Dosierer: kleiner Pumpenkreis mit Tropfen (dosierte Zugabe).
const gDosierpumpe: Glyph = () => (
  <>
    <circle cx="18" cy="20" r="9" />
    <path d="M13 16 L23 20 L13 24 Z" />
    {/* Tropfen am Auslass */}
    <path d="M29 16 q3 4 0 6 q-3 -2 0 -6 Z" />
  </>
);

// Fällmitteldosierung: Vorlagebehälter mit Tropfen in einen Prozessstrom (Chemikalienzugabe).
const gFaellmittel: Glyph = () => (
  <>
    <path d="M12 8 L12 20 L20 20 L20 8" />
    <line x1="10" y1="8" x2="22" y2="8" />
    {/* Dosier-Tropfen */}
    <path d="M16 22 q2.5 3.5 0 5 q-2.5 -1.5 0 -5 Z" />
    {/* Prozessstrom-Rinne darunter */}
    <path d="M8 30 L32 30 M8 34 L32 34" />
  </>
);

// Gebläse / Verdichter (Luft): Kreis mit Lüfterrad (drei Schaufeln).
const gGeblaese: Glyph = () => (
  <>
    <circle cx="20" cy="20" r="11" />
    <path d="M20 20 L20 10" />
    <path d="M20 20 L28.6 25" />
    <path d="M20 20 L11.4 25" />
    <circle cx="20" cy="20" r="2" />
  </>
);

// Gasverdichter / Verdichter (Gas): Kreis mit stilisiertem Verdichter-Rotor (radiale Schaufeln).
const gVerdichter: Glyph = () => (
  <>
    <circle cx="20" cy="20" r="11" />
    <path d="M20 11 L20 14 M20 26 L20 29 M11 20 L14 20 M26 20 L29 20" />
    <path d="M13.6 13.6 L15.7 15.7 M24.3 24.3 L26.4 26.4 M26.4 13.6 L24.3 15.7 M15.7 24.3 L13.6 26.4" />
  </>
);

// BHKW: Motorblock mit Zylindern und Abgas/Schall — als Rechteck mit zwei Kolbenzylindern.
const gBhkw: Glyph = () => (
  <>
    <rect x="8" y="16" width="24" height="16" rx="2" />
    {/* zwei Zylinder oben */}
    <rect x="12" y="9" width="6" height="7" />
    <rect x="22" y="9" width="6" height="7" />
    {/* Grundlinie / Fundament */}
    <line x1="10" y1="32" x2="30" y2="32" />
  </>
);

// Rührwerk: Welle mit Propellerblatt (stehendes/tauchendes Rührwerk).
const gRuehrwerk: Glyph = () => (
  <>
    <line x1="20" y1="8" x2="20" y2="26" />
    <path d="M12 26 L28 26" />
    <path d="M14 30 Q20 24 26 30" />
    {/* Antriebskopf */}
    <rect x="16" y="6" width="8" height="4" />
  </>
);

// Rechen / Feinrechen: geneigte Rinne mit senkrechten Rechenstäben.
const gRechen: Glyph = () => (
  <>
    <path d="M8 12 L8 30 L32 30" />
    {/* Rechenstäbe */}
    <line x1="13" y1="12" x2="13" y2="30" />
    <line x1="18" y1="12" x2="18" y2="30" />
    <line x1="23" y1="12" x2="23" y2="30" />
    <line x1="28" y1="12" x2="28" y2="30" />
  </>
);

// Separator / Feststoffabscheider: liegende Trommel mit Trennlinie (Fest/Flüssig).
const gSeparator: Glyph = () => (
  <>
    <rect x="8" y="14" width="24" height="12" rx="6" />
    <line x1="14" y1="14" x2="14" y2="26" />
    {/* Feststoffaustrag unten, Flüssigkeit oben */}
    <line x1="14" y1="26" x2="10" y2="31" />
    <line x1="26" y1="14" x2="30" y2="9" />
  </>
);

// Wärmetauscher: Kreis mit S-förmiger Rohrschlange (ISA-Wärmetauscher-Symbolik).
const gWaermetauscher: Glyph = () => (
  <>
    <circle cx="20" cy="20" r="12" />
    <path d="M9 20 h4 q2 0 2 -3 t4 0 t4 0 t4 0 q2 0 2 3 h2" />
  </>
);

// Entschwefelung / Gasaufbereitung / Entwässerung: Prozesskolonne (stehender Behälter mit Packung).
const gKolonne: Glyph = () => (
  <>
    <rect x="13" y="8" width="14" height="24" rx="7" />
    {/* Füllkörper-/Packungsandeutung */}
    <line x1="15" y1="15" x2="25" y2="15" />
    <line x1="15" y1="20" x2="25" y2="20" />
    <line x1="15" y1="25" x2="25" y2="25" />
  </>
);

// Schlammentwässerung: Zentrifuge/Presse — liegende Trommel mit Entwässerungsstrahlen.
const gEntwaesserung: Glyph = () => (
  <>
    <rect x="7" y="14" width="26" height="12" rx="6" />
    <circle cx="14" cy="20" r="2" />
    {/* austretendes Zentrat */}
    <line x1="26" y1="26" x2="26" y2="31" />
    <line x1="30" y1="26" x2="30" y2="30" />
  </>
);

// Gasfackel / Notfackel: Schornstein mit Flammenkontur (Abfackeln von Überschussgas).
const gFackel: Glyph = () => (
  <>
    <line x1="16" y1="16" x2="16" y2="33" />
    <line x1="24" y1="16" x2="24" y2="33" />
    {/* Flamme */}
    <path d="M20 6 C15 11 16 15 20 16 C24 15 25 11 20 6 Z" />
  </>
);

// Feststoffeintrag / Dosierer (Feststoff): Trichter/Bunker mit Austragschnecke.
const gFeststoffeintrag: Glyph = () => (
  <>
    <path d="M9 9 L31 9 L22 22 L18 22 Z" />
    {/* Austragschnecke */}
    <rect x="18" y="22" width="14" height="6" rx="3" />
    <path d="M20 25 q2 -2 4 0 t4 0" />
  </>
);

// Schieber / Absperrschieber: Ventilkörper (zwei Dreiecke) mit senkrechter Spindel.
const gSchieber: Glyph = () => (
  <>
    <path d="M10 14 L20 20 L10 26 Z" />
    <path d="M30 14 L20 20 L30 26 Z" />
    <line x1="20" y1="20" x2="20" y2="10" />
    <line x1="16" y1="10" x2="24" y2="10" />
  </>
);

// Stellventil / Regelventil: Ventilkörper mit Membranantrieb (Kreisbogen) obenauf.
const gStellventil: Glyph = () => (
  <>
    <path d="M10 16 L20 22 L10 28 Z" />
    <path d="M30 16 L20 22 L30 28 Z" />
    <line x1="20" y1="22" x2="20" y2="14" />
    {/* Membranantrieb */}
    <path d="M13 14 A7 5 0 0 1 27 14" />
  </>
);

// Klappe / Regelklappe (Luft): Rohrkanal mit schräg gestellter Klappenscheibe.
const gKlappe: Glyph = () => (
  <>
    <line x1="7" y1="13" x2="33" y2="13" />
    <line x1="7" y1="27" x2="33" y2="27" />
    {/* Klappenscheibe mit Achse */}
    <line x1="14" y1="26" x2="26" y2="14" />
    <circle cx="20" cy="20" r="1.6" />
  </>
);

// ── Kategorie-Fallbacks ─────────────────────────────────────────────────────────────────────

// Sensor / Aktor-Sollwert: ISA-Messkreis (Kreis mit Mittellinie), wie bisher.
const gSensor: Glyph = () => (
  <>
    <circle cx="20" cy="20" r="13" />
    <line x1="7" y1="20" x2="33" y2="20" />
  </>
);

// Maschine (Fallback): Zahnrad-freie, generische Maschine — Kreis mit Dreieck (Dreh-/Förderrichtung).
const gMaschine: Glyph = () => (
  <>
    <circle cx="20" cy="20" r="12" />
    <path d="M15 13 L27 20 L15 27 Z" />
  </>
);

// Behälter (Fallback): stehender Rundbehälter mit Kuppel — die häufigste Grundform.
const gBehaelter: Glyph = () => (
  <>
    <path d="M9 15 A11 6 0 0 1 31 15" />
    <line x1="9" y1="15" x2="9" y2="31" />
    <line x1="31" y1="15" x2="31" y2="31" />
    <path d="M9 31 A11 4 0 0 0 31 31" />
  </>
);

// ── Sonderverfahren moderner Kläranlagen ──────────────────────────────────────────────────

// Zulaufspeicher / Stapelbecken: Rechteckbecken mit Wasserspiegel und einlaufendem Pfeil.
const gSpeicherbecken: Glyph = () => (
  <>
    <path d="M7 12 L7 31 L33 31 L33 12" />
    <path d="M7 22 Q13 19 20 22 T33 22" />
    <path d="M14 6 L20 12 L26 6" />
  </>
);

// Bio-P-Becken (anaerober Selektor): geschlossenes Becken mit Rührwerk, ohne Belüftungsblasen.
const gBiopBecken: Glyph = () => (
  <>
    <path d="M7 12 L7 31 L33 31 L33 12 Z" />
    <line x1="20" y1="12" x2="20" y2="26" />
    <line x1="15" y1="26" x2="25" y2="26" />
    <path d="M11 17 Q14 15 17 17" />
  </>
);

// Thermische Schlammhydrolyse: Behälter mit Heizschlange und aufsteigenden Wärmewellen.
const gHydrolyse: Glyph = () => (
  <>
    <rect x="9" y="12" width="22" height="20" rx="2" />
    <path d="M13 18 h14 M13 22 h14 M13 26 h14" strokeDasharray="3 2" />
    <path d="M14 9 q2 -3 4 0 t4 0" />
  </>
);

// MAP-/Struvit-Fällung: Reaktor mit auskristallisierendem Korn (Raute) am Boden.
const gMapFaellung: Glyph = () => (
  <>
    <path d="M11 9 L11 27 L20 33 L29 27 L29 9" />
    <line x1="11" y1="9" x2="29" y2="9" />
    <path d="M20 20 l3 3 l-3 3 l-3 -3 Z" />
    <circle cx="15" cy="15" r="1.2" />
    <circle cx="25" cy="16" r="1.2" />
  </>
);

// Biofilter (Abluft): Gehäuse mit geschichtetem Filtermaterial, Luft von unten nach oben.
const gBiofilter: Glyph = () => (
  <>
    <rect x="7" y="11" width="26" height="18" rx="2" />
    <path d="M10 24 q3 -2 6 0 t6 0 t6 0" />
    <path d="M10 19 q3 -2 6 0 t6 0 t6 0" />
    <path d="M13 33 v-4 M20 33 v-4 M27 33 v-4" strokeDasharray="2 2" />
    <path d="M20 11 v-4" />
  </>
);

// ── Thermische Abfallbehandlung (MHKW) ────────────────────────────────────────────────────

// Müllbunker: tiefe Grube (Trapez) mit Schütthaufen.
const gMuellbunker: Glyph = () => (
  <>
    <path d="M7 10 L11 32 L29 32 L33 10" />
    <path d="M12 27 q4 -6 8 -2 q4 -5 8 2" />
  </>
);

// Müllkran: Portalträger mit Seil und geöffnetem Greifer.
const gMuellkran: Glyph = () => (
  <>
    <line x1="6" y1="9" x2="34" y2="9" />
    <line x1="20" y1="9" x2="20" y2="19" />
    <path d="M14 19 L20 27 L26 19" />
    <path d="M14 19 L12 24 M26 19 L28 24" />
  </>
);

// Rostfeuerung: schräger Stufenrost mit Flamme darüber.
const gRostfeuerung: Glyph = () => (
  <>
    <path d="M8 30 L14 30 L14 26 L20 26 L20 22 L26 22 L26 18 L32 18" />
    <path d="M16 16 q1 -5 4 -7 q0 4 3 5 q2 2 0 5" />
  </>
);

// Abhitzekessel / Dampferzeuger: Trommel mit Siederohren und Dampfabgang.
const gDampfkessel: Glyph = () => (
  <>
    <rect x="9" y="14" width="22" height="16" rx="3" />
    <path d="M14 14 v16 M20 14 v16 M26 14 v16" />
    <path d="M20 14 v-5" />
    <path d="M17 9 q3 -3 6 0" />
  </>
);

// Dampfturbine: Turbinen-Trapez mit Welle und Generator-Kreis.
const gDampfturbine: Glyph = () => (
  <>
    <path d="M8 14 L22 18 L22 24 L8 28 Z" />
    <line x1="22" y1="21" x2="26" y2="21" />
    <circle cx="30" cy="21" r="4" />
  </>
);

// Entstickung (SCR/SNCR): Kanal mit Katalysator-Wabe und Reagenz-Eindüsung.
const gScrDenox: Glyph = () => (
  <>
    <rect x="8" y="13" width="24" height="18" rx="1" />
    <path d="M14 13 v18 M20 13 v18 M26 13 v18 M8 22 h24" />
    <path d="M20 13 v-6 M17 9 l3 -3 l3 3" />
  </>
);

// Rauchgaswäscher / Sprühabsorber: Turm mit Sprühdüsen und Tropfen.
const gRauchgaswaesche: Glyph = () => (
  <>
    <path d="M12 10 L12 30 L28 30 L28 10" />
    <path d="M12 10 A8 4 0 0 1 28 10" />
    <line x1="20" y1="12" x2="20" y2="17" />
    <path d="M16 20 l-1 2 M20 20 l-1 2 M24 20 l-1 2" />
    <path d="M15 17 h10" />
  </>
);

// Gewebefilter: Gehäuse mit hängenden Filterschläuchen.
const gGewebefilter: Glyph = () => (
  <>
    <rect x="8" y="9" width="24" height="18" rx="1" />
    <path d="M13 12 v12 M18 12 v12 M23 12 v12 M28 12 v12" />
    <path d="M14 27 L20 33 L26 27" />
  </>
);

// Schlacke-/Aschebunker: Grube mit körnigem Schüttgut.
const gSchlackebunker: Glyph = () => (
  <>
    <path d="M8 12 L12 31 L28 31 L32 12" />
    <circle cx="16" cy="26" r="1.3" />
    <circle cx="21" cy="27" r="1.3" />
    <circle cx="25" cy="25" r="1.3" />
    <circle cx="19" cy="23" r="1.3" />
  </>
);

// Kamin: hoher, sich verjüngender Schornstein mit Fahne.
const gKamin: Glyph = () => (
  <>
    <path d="M15 33 L17 10 L23 10 L25 33" />
    <line x1="16" y1="14" x2="24" y2="14" />
    <path d="M25 8 q4 -3 7 0" />
  </>
);

// ── Brauerei ──────────────────────────────────────────────────────────────────────────────

// Schrotmühle: Trichter über zwei Walzen.
const gSchrotmuehle: Glyph = () => (
  <>
    <path d="M9 8 L31 8 L23 18 L17 18 Z" />
    <circle cx="15" cy="24" r="5" />
    <circle cx="26" cy="24" r="5" />
    <path d="M20 30 v4" />
  </>
);

// Maischbottich: Bottich mit Rührwerk und Heizschlange.
const gMaischebottich: Glyph = () => (
  <>
    <path d="M9 12 L9 28 A11 5 0 0 0 31 28 L31 12" />
    <line x1="9" y1="12" x2="31" y2="12" />
    <line x1="20" y1="7" x2="20" y2="24" />
    <line x1="15" y1="24" x2="25" y2="24" />
    <path d="M12 20 h4 M24 20 h4" strokeDasharray="2 2" />
  </>
);

// Läuterbottich: Bottich mit Senkboden (gestrichelt) und Ablauf.
const gLaeuterbottich: Glyph = () => (
  <>
    <path d="M9 11 L9 29 L31 29 L31 11" />
    <line x1="9" y1="11" x2="31" y2="11" />
    <line x1="10" y1="24" x2="30" y2="24" strokeDasharray="2 2" />
    <path d="M20 29 v4" />
  </>
);

// Würzepfanne: Sudpfanne mit Brüdenkamin und kochender Würze.
const gWuerzepfanne: Glyph = () => (
  <>
    <path d="M10 14 L10 27 A10 5 0 0 0 30 27 L30 14" />
    <line x1="10" y1="14" x2="30" y2="14" />
    <rect x="17" y="6" width="6" height="8" />
    <path d="M14 21 q2 -2 4 0 t4 0 t4 0" />
  </>
);

// Whirlpool: Zylinder mit Wirbel (Spirale) und Trubkegel.
const gWhirlpool: Glyph = () => (
  <>
    <path d="M10 12 L10 28 L30 28 L30 12" />
    <ellipse cx="20" cy="12" rx="10" ry="3.5" />
    <path d="M23 19 a3.5 3.5 0 1 1 -3 -3.5" />
    <path d="M17 28 L20 24 L23 28" />
  </>
);

// Würzekühler: Plattenwärmetauscher — Plattenpaket mit gegenläufigen Strömen.
const gWuerzekuehler: Glyph = () => (
  <>
    <rect x="11" y="10" width="18" height="20" rx="1" />
    <path d="M15 10 v20 M20 10 v20 M25 10 v20" />
    <path d="M8 15 h3 M29 15 h3" />
    <path d="M8 25 h3 M29 25 h3" />
  </>
);

// Gär-/Lagertank (ZKG): zylindro-konischer Tank mit Kühlmantel-Zone.
const gGaertank: Glyph = () => (
  <>
    <path d="M12 11 L12 25 L20 34 L28 25 L28 11" />
    <ellipse cx="20" cy="11" rx="8" ry="3" />
    <path d="M12 16 h16 M12 21 h16" strokeDasharray="2 2" />
  </>
);

// Hefegabe: kleiner Dosierbehälter mit Hefezellen und Abgabepfeil.
const gHefegabe: Glyph = () => (
  <>
    <path d="M13 9 L13 22 L20 28 L27 22 L27 9 Z" />
    <circle cx="18" cy="15" r="1.5" />
    <circle cx="23" cy="17" r="1.5" />
    <path d="M20 28 v5 M17 30 l3 3 l3 -3" />
  </>
);

// Bierfiltration: Filtergehäuse mit Filterkerzen und Durchfluss.
const gFiltrationBier: Glyph = () => (
  <>
    <rect x="12" y="10" width="16" height="20" rx="2" />
    <path d="M16 13 v14 M20 13 v14 M24 13 v14" strokeDasharray="3 2" />
    <path d="M7 20 h5 M28 20 h5" />
  </>
);

// Drucktank: liegender Druckbehälter mit gewölbten Böden.
const gDrucktank: Glyph = () => (
  <>
    <path d="M12 13 L28 13 A5 7 0 0 1 28 27 L12 27 A5 7 0 0 1 12 13" />
    <path d="M12 13 A5 7 0 0 0 12 27" />
    <line x1="20" y1="13" x2="20" y2="9" />
  </>
);

// CIP-Station: zwei Vorlagebehälter mit Kreislaufpfeil.
const gCipStation: Glyph = () => (
  <>
    <rect x="7" y="9" width="10" height="13" rx="1" />
    <rect x="23" y="9" width="10" height="13" rx="1" />
    <path d="M12 22 v6 h16 v-6" />
    <path d="M25 25 l3 3 l-3 3" />
  </>
);

// Kältemaschine: Verdichter-Kreis am Aggregat mit Schneeflocke.
const gKaeltemaschine: Glyph = () => (
  <>
    <rect x="7" y="12" width="16" height="16" rx="2" />
    <circle cx="15" cy="20" r="4" />
    <path d="M29 12 v14 M24 15 l10 8 M34 15 l-10 8" />
  </>
);

// ── Molkerei ──────────────────────────────────────────────────────────────────────────────

// Rohmilchtank: liegendes Silo auf Füßen.
const gRohmilchtank: Glyph = () => (
  <>
    <path d="M11 13 L29 13 A5 6 0 0 1 29 25 L11 25 A5 6 0 0 1 11 13" />
    <path d="M11 13 A5 6 0 0 0 11 25" />
    <path d="M14 25 v6 M26 25 v6" />
  </>
);

// Pasteur: Plattenapparat mit Heißhalteschleife.
const gPasteur: Glyph = () => (
  <>
    <rect x="8" y="12" width="14" height="16" rx="1" />
    <path d="M12 12 v16 M16 12 v16" />
    <path d="M22 17 h5 a4 4 0 0 1 0 8 h-5" />
  </>
);

// Homogenisator: Hochdruck-Kolbenblock mit Ventilspalt.
const gHomogenisator: Glyph = () => (
  <>
    <rect x="9" y="14" width="14" height="13" rx="1" />
    <path d="M13 14 v-5 M18 14 v-5" />
    <path d="M23 17 L30 17 M23 24 L30 24" />
    <path d="M27 17 L27 24" />
  </>
);

// Eindampfer: Rohrbündel mit Brüdenabscheider oben.
const gEindampfer: Glyph = () => (
  <>
    <rect x="10" y="16" width="12" height="16" rx="1" />
    <path d="M14 16 v16 M18 16 v16" />
    <circle cx="28" cy="14" r="5" />
    <path d="M28 9 v-3" />
    <path d="M22 20 L23 14 L23 14" />
  </>
);

// Sprühtrockner: konische Trockenkammer mit Zerstäuberdüse.
const gSpruehtrockner: Glyph = () => (
  <>
    <path d="M10 10 L30 10 L30 20 L20 33 L10 20 Z" />
    <path d="M20 6 v4" />
    <path d="M17 15 l-2 4 M20 15 v4 M23 15 l2 4" />
  </>
);

// ── Bäder / Thermen ───────────────────────────────────────────────────────────────────────

// Schwimmbecken: Beckenquerschnitt mit Wasserlinie und Einstiegsleiter.
const gSchwimmbecken: Glyph = () => (
  <>
    <path d="M6 14 L6 30 L34 30 L34 14" />
    <path d="M6 19 q7 -3 14 0 t14 0" />
    <path d="M27 14 v-5 M31 14 v-5 M27 11 h4" />
  </>
);

// Schwallwasserbehälter: geschlossener Behälter mit Wellenlinie.
const gSchwallwasserbehaelter: Glyph = () => (
  <>
    <rect x="8" y="11" width="24" height="19" rx="2" />
    <path d="M11 22 q4 -3 7 0 t7 0 t7 0" />
    <path d="M20 11 v-4" />
  </>
);

// Mehrschichtfilter: Druckkessel mit drei Filterschichten.
const gFilterMehrschicht: Glyph = () => (
  <>
    <path d="M11 13 L11 27 A9 4 0 0 0 29 27 L29 13 A9 4 0 0 0 11 13" />
    <path d="M11 19 h18" />
    <path d="M11 23 h18" strokeDasharray="2 2" />
    <path d="M20 9 v-3 M20 31 v3" />
  </>
);

// Flockungsmitteldosierung: Dosierbehälter mit fallendem Tropfen.
const gFlockungsdosierung: Glyph = () => (
  <>
    <path d="M13 8 L27 8 L27 20 L20 27 L13 20 Z" />
    <path d="M20 30 q-2 3 0 4 q2 -1 0 -4" />
  </>
);

// Chlorung / Desinfektion: Gasflasche mit Dosierleitung in den Strom.
const gChlorung: Glyph = () => (
  <>
    <path d="M13 14 L13 30 A7 3 0 0 0 27 30 L27 14 A7 3 0 0 0 13 14" />
    <rect x="18" y="7" width="4" height="5" />
    <path d="M27 20 h6" />
    <path d="M30 17 l3 3 l-3 3" />
  </>
);

// pH-Korrektur: zwei Dosierbehälter (Säure/Lauge) mit + und −.
const gPhKorrektur: Glyph = () => (
  <>
    <path d="M7 9 L17 9 L17 21 L12 27 L7 21 Z" />
    <path d="M23 9 L33 9 L33 21 L28 27 L23 21 Z" />
    <path d="M10 14 h4 M12 12 v4" />
    <path d="M26 14 h4" />
  </>
);

// UV-Desinfektion: Reaktorrohr mit Strahler und abgehenden Strahlen.
const gUvDesinfektion: Glyph = () => (
  <>
    <rect x="8" y="15" width="24" height="10" rx="5" />
    <line x1="14" y1="20" x2="26" y2="20" />
    <path d="M17 15 v-4 M23 15 v-4 M17 25 v4 M23 25 v4" />
  </>
);

// Thermalwasser-Quelle: Bohrung mit aufsteigendem Wasser und Wärmewellen.
const gThermalwasserQuelle: Glyph = () => (
  <>
    <path d="M14 33 L14 18 A6 6 0 0 1 26 18 L26 33" />
    <path d="M20 18 v-6" />
    <path d="M16 10 q2 -3 4 0 t4 0" />
    <path d="M16 5 q2 -3 4 0 t4 0" />
  </>
);

// ── Fernwärme / Wärmenetz ─────────────────────────────────────────────────────────────────

// Wärmespeicher: stehender Schichtenspeicher mit Temperaturschichtung.
const gWaermespeicher: Glyph = () => (
  <>
    <path d="M12 10 L12 30 A8 3 0 0 0 28 30 L28 10 A8 3 0 0 0 12 10" />
    <path d="M12 17 h16 M12 22 h16 M12 27 h16" strokeDasharray="3 2" />
  </>
);

// Heizkessel: Kesselkörper mit Flamme und Abgasabgang.
const gHeizkessel: Glyph = () => (
  <>
    <rect x="9" y="12" width="20" height="20" rx="2" />
    <path d="M17 27 q1 -5 3 -7 q0 3 2 4 q2 2 0 3" />
    <path d="M29 16 h4 v-8" />
  </>
);

// Großwärmepumpe: Kreisprozess mit Verdichter und Wärme-Hub-Pfeilen.
const gWaermepumpe: Glyph = () => (
  <>
    <circle cx="20" cy="20" r="11" />
    <path d="M20 9 L26 15 L20 20 L14 25 L20 31" />
    <path d="M6 26 h5 M29 14 h5" />
  </>
);

// Wärmeübergabestation: Plattenwärmetauscher mit Primär-/Sekundärkreis und Stellventil.
const gWaermeuebergabe: Glyph = () => (
  <>
    <rect x="14" y="11" width="12" height="18" rx="1" />
    <path d="M18 11 v18 M22 11 v18" />
    <path d="M7 15 h7 M26 15 h7" />
    <path d="M7 25 h7 M26 25 h7" />
    <path d="M8 12 l4 3 l-4 3 Z" />
  </>
);

// ── Wasserwerk ────────────────────────────────────────────────────────────────────────────

// Förderbrunnen: Bohrbrunnen mit Verrohrung, Pumpe und Grundwasserspiegel.
const gBrunnen: Glyph = () => (
  <>
    <path d="M15 8 L15 33 M25 8 L25 33" />
    <path d="M6 16 q7 -2 14 0 t14 0" strokeDasharray="3 2" />
    <rect x="17" y="22" width="6" height="7" rx="1" />
    <path d="M20 22 v-8" />
  </>
);

// Belüftung / Entsäuerung: Kaskadenstufen mit aufsteigenden Luftblasen.
const gBelueftungEntsaeuerung: Glyph = () => (
  <>
    <path d="M7 12 L15 12 L15 20 L23 20 L23 28 L33 28" />
    <circle cx="12" cy="24" r="1.4" />
    <circle cx="19" cy="27" r="1.4" />
    <circle cx="27" cy="22" r="1.4" />
  </>
);

// Aktivkohlefilter: Druckkessel mit körniger Kohleschüttung.
const gAktivkohlefilter: Glyph = () => (
  <>
    <path d="M11 13 L11 27 A9 4 0 0 0 29 27 L29 13 A9 4 0 0 0 11 13" />
    <circle cx="16" cy="19" r="1.3" />
    <circle cx="21" cy="21" r="1.3" />
    <circle cx="25" cy="18" r="1.3" />
    <circle cx="19" cy="24" r="1.3" />
    <path d="M20 9 v-3 M20 31 v3" />
  </>
);

// Reinwasserbehälter: abgedeckter Speicher mit Wasserstand.
const gReinwasserbehaelter: Glyph = () => (
  <>
    <path d="M7 13 L7 30 L33 30 L33 13 Z" />
    <path d="M5 13 L20 7 L35 13" />
    <path d="M10 22 q5 -3 10 0 t10 0" />
  </>
);

// ── Register: Symbol-ID → Glyph ─────────────────────────────────────────────────────────────
//
// Datengetriebene Zuordnung. Mehrere Katalog-IDs teilen sich bewusst ein Glyph, wenn sie dieselbe
// P&ID-Grundform tragen (z. B. alle Rundklärer, alle Gasspeicher/-hauben). Neue Katalog-Symbole,
// die hier fehlen, fallen sauber auf das Kategorie-Icon zurück (siehe glyphFor).
// ── Oberflächentechnik / Metallverarbeitung ───────────────────────────────────────────────

// Entfettung: Bad mit Wärmezeichen (beheizt) und Tropfen für das gelöste Fett.
const gEntfettungsbad: Glyph = () => (
  <>
    <path d="M7 14 L7 31 A2 2 0 0 0 9 33 L31 33 A2 2 0 0 0 33 31 L33 14" />
    <path d="M7 19 C12 17 16 21 20 19 C24 17 28 21 33 19" />
    <line x1="20" y1="5" x2="20" y2="11" />
    <rect x="15" y="11" width="10" height="7" />
    {/* Heizschlange am Wannenboden */}
    <path d="M11 29 C14 26 17 32 20 29 C23 26 26 32 29 29" />
  </>
);

// Beize: Bad mit Säure-Tropfen über dem Spiegel.
const gBeizbad: Glyph = () => (
  <>
    <path d="M7 14 L7 31 A2 2 0 0 0 9 33 L31 33 A2 2 0 0 0 33 31 L33 14" />
    <path d="M7 20 C12 18 16 22 20 20 C24 18 28 22 33 20" />
    <path d="M20 5 L23 11 A3.4 3.4 0 1 1 17 11 Z" />
  </>
);

// Passivierung: Bad mit Schichtsymbol (dünne Auflage auf der Ware).
const gPassivierbad: Glyph = () => (
  <>
    <path d="M7 14 L7 31 A2 2 0 0 0 9 33 L31 33 A2 2 0 0 0 33 31 L33 14" />
    <path d="M7 20 C12 18 16 22 20 20 C24 18 28 22 33 20" />
    <rect x="14" y="7" width="12" height="6" />
    <line x1="14" y1="10" x2="26" y2="10" strokeDasharray="2 2" />
  </>
);

// Spülbad / Kaskade: Wanne mit überlaufender Stufe (Gegenstrom).
const gSpuelbad: Glyph = () => (
  <>
    <path d="M6 16 L6 30 A2 2 0 0 0 8 32 L19 32 L19 16" />
    <path d="M21 20 L21 30 A2 2 0 0 0 23 32 L34 32 L34 20" />
    <path d="M6 21 C10 19 15 23 19 21" />
    <path d="M21 25 C25 23 30 27 34 25" />
    {/* Überlauf von der hinteren in die vordere Stufe */}
    <path d="M21 20 L19 20 L19 17" />
  </>
);

// Haftwassertrockner: Tunnel mit Wärmewellen und aufsteigendem Dampf.
const gTrockner: Glyph = () => (
  <>
    <rect x="6" y="13" width="28" height="16" rx="2" />
    <line x1="6" y1="29" x2="34" y2="29" />
    <path d="M13 25 C15 22 17 26 19 23" />
    <path d="M21 25 C23 22 25 26 27 23" />
    {/* Dampfabzug */}
    <path d="M16 12 C16 9 19 9 19 6" />
    <path d="M23 12 C23 9 26 9 26 6" />
  </>
);

// Pulverkabine: Kabine mit Sprühpistole und Pulverwolke auf das Werkstück.
const gPulverkabine: Glyph = () => (
  <>
    <path d="M8 10 L8 31 L32 31 L32 10" />
    <line x1="6" y1="10" x2="34" y2="10" />
    {/* Werkstück am Gehänge */}
    <line x1="24" y1="12" x2="24" y2="16" />
    <rect x="21" y="16" width="6" height="10" />
    {/* Pistole mit Sprühkegel */}
    <path d="M10 18 L15 20 L10 22 Z" />
    <path d="M15 17 L20 15" strokeDasharray="1.5 2" />
    <path d="M15 21 L20 21" strokeDasharray="1.5 2" />
    <path d="M15 25 L20 27" strokeDasharray="1.5 2" />
  </>
);

// Pulver-Rückgewinnung: Zyklon (Trichter mit Tangentialeintritt).
const gPulverzyklon: Glyph = () => (
  <>
    <path d="M12 8 L28 8 L28 18 L20 32 L12 18 Z" />
    <line x1="20" y1="8" x2="20" y2="14" />
    <path d="M28 12 L34 12" />
    <path d="M12 12 L6 12" />
  </>
);

// Einbrennofen: Tunnelofen mit Flamme und durchlaufender Ware.
const gEinbrennofen: Glyph = () => (
  <>
    <rect x="5" y="11" width="30" height="18" rx="2" />
    {/* Durchlauf */}
    <line x1="5" y1="15" x2="35" y2="15" strokeDasharray="3 2" />
    <rect x="16" y="17" width="8" height="7" />
    {/* Brennerflamme */}
    <path d="M11 27 C9 24 12 23 11 20 C14 22 14 25 12 27" />
    <path d="M29 27 C27 24 30 23 29 20 C32 22 32 25 30 27" />
  </>
);

// Power-and-Free-Hängebahn: Laufschiene mit zwei Gehängen.
const gHaengebahn: Glyph = () => (
  <>
    <line x1="4" y1="11" x2="36" y2="11" />
    <circle cx="13" cy="14" r="2.4" />
    <circle cx="27" cy="14" r="2.4" />
    <line x1="13" y1="16.4" x2="13" y2="22" />
    <line x1="27" y1="16.4" x2="27" y2="22" />
    <rect x="9" y="22" width="8" height="9" />
    <rect x="23" y="22" width="8" height="9" />
  </>
);

// Rollformlinie: Bandeinlauf zwischen Rollenpaaren, Profil am Auslauf.
const gRollformlinie: Glyph = () => (
  <>
    <line x1="4" y1="20" x2="30" y2="20" />
    <circle cx="12" cy="15" r="3.2" />
    <circle cx="12" cy="25" r="3.2" />
    <circle cx="22" cy="15" r="3.2" />
    <circle cx="22" cy="25" r="3.2" />
    {/* fertiges Hohlkammerprofil */}
    <rect x="30" y="16" width="7" height="8" rx="1.5" />
  </>
);

// PU-Mischkopf: zwei Komponentenzuläufe, Mischkammer, Austrag ins Profil.
const gSchaeumkopf: Glyph = () => (
  <>
    <line x1="10" y1="6" x2="16" y2="14" />
    <line x1="30" y1="6" x2="24" y2="14" />
    <rect x="14" y="14" width="12" height="10" rx="1.5" />
    {/* Mischwendel */}
    <path d="M17 16 C22 18 18 21 23 22" />
    <path d="M20 24 L20 28" />
    <path d="M13 31 L27 31" />
    <path d="M15 28 C17 30 23 30 25 28" />
  </>
);

// Druckluftbehälter: stehender Druckbehälter mit gewölbten Böden und Manometer.
const gDruckluftbehaelter: Glyph = () => (
  <>
    <path d="M12 12 A8 4 0 0 1 28 12 L28 28 A8 4 0 0 1 12 28 Z" />
    <path d="M12 12 A8 4 0 0 0 28 12" />
    <circle cx="20" cy="20" r="3.2" />
    <line x1="20" y1="20" x2="22" y2="18" />
    <line x1="28" y1="20" x2="34" y2="20" />
    <line x1="6" y1="20" x2="12" y2="20" />
  </>
);

// Kältetrockner: Kältemaschine mit Kondensattropfen am Austritt.
const gDrucklufttrockner: Glyph = () => (
  <>
    <rect x="7" y="12" width="26" height="16" rx="2" />
    {/* Schneeflocke = Kälteerzeugung */}
    <line x1="20" y1="15" x2="20" y2="23" />
    <line x1="16.5" y1="17" x2="23.5" y2="21" />
    <line x1="16.5" y1="21" x2="23.5" y2="17" />
    {/* Kondensatablass */}
    <path d="M20 28 L20 31" />
    <path d="M20 31 L22 34 A2.4 2.4 0 1 1 18 34 Z" />
  </>
);

// Neutralisation: Reaktionsbecken mit Rührer und zwei Dosierzuläufen (Lauge/Säure).
const gNeutralisation: Glyph = () => (
  <>
    <path d="M8 13 L8 30 A2 2 0 0 0 10 32 L30 32 A2 2 0 0 0 32 30 L32 13" />
    <path d="M8 18 C12 16 16 20 20 18 C24 16 28 20 32 18" />
    {/* Rührwerkswelle */}
    <line x1="20" y1="7" x2="20" y2="26" />
    <line x1="16" y1="26" x2="24" y2="26" />
    {/* zwei Dosierzuläufe */}
    <line x1="11" y1="7" x2="11" y2="13" />
    <line x1="29" y1="7" x2="29" y2="13" />
  </>
);


// ══════════════════════════════════════════════════════════════════════════════════════════
//  Wärmenetz-Hydraulik, Quartier/Anergienetz, Druckluft, Kälte, Gebäudetechnik
// ══════════════════════════════════════════════════════════════════════════════════════════

// Rohrleitungsabschnitt: Vor- und Rücklauf als Doppelstrang mit Dämmschraffur.
const gRohrleitungsabschnitt: Glyph = () => (
  <>
    <path d="M5 15 h30 M5 25 h30" />
    <path d="M11 12 v6 M19 12 v6 M27 12 v6" strokeDasharray="2 2" />
    <path d="M11 22 v6 M19 22 v6 M27 22 v6" strokeDasharray="2 2" />
  </>
);

// Netzpumpe: Kreiselpumpen-Kreis mit Laufrad auf der Leitung.
const gNetzpumpe: Glyph = () => (
  <>
    <circle cx="20" cy="20" r="9" />
    <path d="M20 11 L20 20 L28 24" />
    <path d="M4 20 h7 M29 20 h7" />
  </>
);

// Druckhaltestation: Membranbehälter mit Trennmembran und Netzanbindung.
const gDruckhaltung: Glyph = () => (
  <>
    <rect x="12" y="9" width="16" height="22" rx="8" />
    <path d="M12 20 q8 -5 16 0" />
    <path d="M20 31 v5 M14 36 h12" />
  </>
);

// Verteiler/Sammler: waagerechter Balken mit drei Abgängen.
const gVerteilerSammler: Glyph = () => (
  <>
    <rect x="6" y="15" width="28" height="8" rx="3" />
    <path d="M12 23 v8 M20 23 v8 M28 23 v8" />
    <path d="M6 19 h-3" />
  </>
);

// Tiefengeothermie-Dublette: Förder- und Injektionsbohrung mit Reservoir-Schraffur.
const gGeothermieDublette: Glyph = () => (
  <>
    <path d="M13 6 v20 M27 6 v20" />
    <path d="M13 26 q7 6 14 0" />
    <path d="M6 31 h28 M8 35 h24" strokeDasharray="3 2" />
    <path d="M10 9 l3 -3 l3 3" />
    <path d="M24 6 l3 3 l3 -3" />
  </>
);

// Solarthermie-Kollektorfeld: geneigter Absorber mit Einstrahlungspfeilen.
const gSolarthermieFeld: Glyph = () => (
  <>
    <path d="M8 30 L16 14 L34 14 L26 30 Z" />
    <path d="M14 22 h14 M17 18 h14" />
    <path d="M6 10 l3 3 M12 6 l1 4 M4 16 l4 1" />
  </>
);

// Elektrodenkessel: Kesselkörper mit Elektroden und Blitz (Power-to-Heat).
const gElektrodenkessel: Glyph = () => (
  <>
    <rect x="10" y="11" width="20" height="21" rx="2" />
    <path d="M15 15 v9 M20 15 v9 M25 15 v9" />
    <path d="M22 4 L17 11 h5 L20 17" />
  </>
);

// Abwärmequelle: Serverschrank mit aufsteigenden Wärmewellen.
const gAbwaermequelle: Glyph = () => (
  <>
    <rect x="10" y="16" width="20" height="18" rx="2" />
    <path d="M14 21 h12 M14 25 h12 M14 29 h8" />
    <path d="M15 12 q2 -3 0 -6 M20 12 q2 -3 0 -6 M25 12 q2 -3 0 -6" />
  </>
);

// Gebäude/Hausanschluss: Haus mit Übergabestation im Sockel.
const gGebaeudeAnschluss: Glyph = () => (
  <>
    <path d="M8 20 L20 9 L32 20" />
    <path d="M11 20 v14 h18 v-14" />
    <rect x="16" y="25" width="8" height="9" rx="1" />
    <path d="M5 30 h6 M29 30 h6" />
  </>
);

// Dezentrale Wärmepumpe: kompakter WP-Kreis im Haus-Umriss.
const gWaermepumpeDezentral: Glyph = () => (
  <>
    <path d="M9 17 L20 8 L31 17" />
    <circle cx="20" cy="25" r="7" />
    <path d="M20 18 L24 22 L20 25 L16 28 L20 32" />
  </>
);

// Erdsondenfeld: Bohrsonden im Erdreich mit U-Rohr-Fuß.
const gErdsondenfeld: Glyph = () => (
  <>
    <path d="M5 11 h30" strokeDasharray="3 2" />
    <path d="M11 11 v18 q0 3 3 3 q3 0 3 -3 v-18" />
    <path d="M23 11 v14 q0 3 3 3 q3 0 3 -3 v-14" />
    <path d="M6 35 h28" strokeDasharray="2 3" />
  </>
);

// Flächenkollektor: mäandrierendes Rohr unter der Geländeoberkante.
const gErdwaermekollektor: Glyph = () => (
  <>
    <path d="M4 10 h32" strokeDasharray="3 2" />
    <path d="M8 18 h20 q4 0 4 4 q0 4 -4 4 H10 q-4 0 -4 4 q0 4 4 4 h20" />
  </>
);

// PV-Feld: Modulfläche mit Zellraster und Einstrahlung.
const gPvFeld: Glyph = () => (
  <>
    <path d="M7 32 L14 16 L33 16 L26 32 Z" />
    <path d="M12 24 h16 M18 16 L14 32 M24 16 L20 32" />
    <path d="M5 9 l3 3 M13 5 l1 4" />
  </>
);

// Batteriespeicher: Zellblock mit Pol und Ladebalken.
const gBatteriespeicher: Glyph = () => (
  <>
    <rect x="8" y="12" width="24" height="20" rx="2" />
    <path d="M15 9 v3 M25 9 v3" />
    <path d="M12 18 h6 M12 23 h6 M12 28 h6" />
    <path d="M27 17 l-4 6 h4 l-4 6" />
  </>
);

// Netzanschlusspunkt: Trafo-Doppelkreis mit Sammelschiene.
const gNetzanschlusspunkt: Glyph = () => (
  <>
    <circle cx="16" cy="20" r="7" />
    <circle cx="24" cy="20" r="7" />
    <path d="M6 8 h28" />
    <path d="M20 8 v5" />
  </>
);

// Freikühler: Lamellenblock mit Lüfterrad.
const gFreikuehler: Glyph = () => (
  <>
    <rect x="6" y="14" width="28" height="14" rx="2" />
    <path d="M11 14 v14 M16 14 v14 M21 14 v14 M26 14 v14" />
    <circle cx="20" cy="8" r="5" />
    <path d="M20 3 v10 M15 8 h10" />
  </>
);

// Gebäudekühlung: Raum mit Schneeflocken-Andeutung.
const gGebaeudekuehlung: Glyph = () => (
  <>
    <rect x="8" y="12" width="24" height="20" rx="2" />
    <path d="M20 16 v12 M15 19 l10 6 M25 19 l-10 6" />
  </>
);

// Schraubenkompressor: Gehäuse mit zwei ineinandergreifenden Rotoren.
const gSchraubenkompressor: Glyph = () => (
  <>
    <rect x="8" y="13" width="24" height="16" rx="3" />
    <circle cx="16" cy="21" r="5" />
    <circle cx="24" cy="21" r="5" />
    <path d="M4 21 h4 M32 21 h4" />
  </>
);

// Adsorptionstrockner: Doppelbehälter (Trocknung/Regeneration) mit Trockenmittel.
const gAdsorptionstrockner: Glyph = () => (
  <>
    <rect x="8" y="10" width="10" height="22" rx="4" />
    <rect x="22" y="10" width="10" height="22" rx="4" />
    <path d="M11 17 h4 M11 22 h4 M25 17 h4 M25 22 h4" strokeDasharray="2 2" />
    <path d="M18 14 h4" />
  </>
);

// Filterlinie: Filterpatrone mit Durchströmung und Differenzdruck.
const gFilterlinie: Glyph = () => (
  <>
    <path d="M14 10 h12 v20 h-12 z" />
    <path d="M17 10 v20 M20 10 v20 M23 10 v20" strokeDasharray="2 2" />
    <path d="M5 20 h9 M26 20 h9" />
  </>
);

// Kondensatableiter: Sammeltopf mit Schwimmer und Abgang.
const gKondensatableiter: Glyph = () => (
  <>
    <path d="M11 11 h18 v12 q0 6 -9 6 q-9 0 -9 -6 z" />
    <circle cx="20" cy="21" r="3" />
    <path d="M20 29 v6 M16 35 h8" />
  </>
);

// Druckluft-Ringleitung: geschlossener Ring mit zwei Abnahmestellen.
const gDruckluftVerteilnetz: Glyph = () => (
  <>
    <rect x="8" y="12" width="24" height="16" rx="8" />
    <path d="M14 28 v7 M26 28 v7" />
    <path d="M20 12 v-6" />
  </>
);

// Verflüssiger: Rohrbündel im Mantel mit Kondensat-Abgang.
const gVerfluessiger: Glyph = () => (
  <>
    <rect x="7" y="14" width="26" height="13" rx="6" />
    <path d="M12 17 h16 M12 21 h16 M12 25 h16" />
    <path d="M20 27 v7" />
  </>
);

// Rückkühlwerk: Kühlturm-Silhouette mit Luftaustritt.
const gRueckkuehlwerk: Glyph = () => (
  <>
    <path d="M11 33 L14 19 h12 l3 14 z" />
    <path d="M13 19 h14" />
    <path d="M16 15 q2 -4 0 -8 M24 15 q2 -4 0 -8" />
  </>
);

// Eisspeicher: Behälter mit Wärmetauscher-Wendel und Eiskristall.
const gEisspeicher: Glyph = () => (
  <>
    <rect x="9" y="10" width="22" height="22" rx="3" />
    <path d="M13 16 q7 4 14 0 M13 22 q7 4 14 0 M13 28 q7 4 14 0" />
    <path d="M20 13 v6 M17 15 l6 3 M23 15 l-6 3" />
  </>
);

// Kühlzelle: Kammer mit Tür und Temperaturmarke.
const gKuehlzelle: Glyph = () => (
  <>
    <rect x="7" y="10" width="26" height="22" rx="2" />
    <path d="M24 10 v22" />
    <circle cx="27" cy="21" r="1.4" />
    <path d="M12 17 v8 M15 20 l-3 -3 l-3 3" />
  </>
);

// Kälteverdichtersatz: zwei Verdichter auf gemeinsamem Sammler.
const gVerdichtersatzKaelte: Glyph = () => (
  <>
    <circle cx="14" cy="15" r="6" />
    <circle cx="27" cy="15" r="6" />
    <path d="M14 9 L18 15 L14 21" />
    <path d="M27 9 L31 15 L27 21" />
    <path d="M8 28 h26 M14 21 v7 M27 21 v7" />
  </>
);

// RLT-Anlage: Gehäuse mit Ventilator, Register und Wärmerückgewinnung.
const gRltAnlage: Glyph = () => (
  <>
    <rect x="5" y="12" width="30" height="17" rx="2" />
    <circle cx="13" cy="20" r="4" />
    <path d="M13 16 v8 M9 20 h8" />
    <path d="M21 13 v15 M25 13 v15" />
    <path d="M29 15 l4 10 M33 15 l-4 10" />
  </>
);

// Heizkreis: Dreiwegemischer mit Kreispumpe.
const gHeizkreis: Glyph = () => (
  <>
    <path d="M20 8 v9 M8 24 h9 M32 24 h-9" />
    <path d="M17 17 L23 23 M23 17 L17 23" />
    <circle cx="20" cy="30" r="5" />
    <path d="M20 25 v5 L24 32" />
  </>
);

// Raumgerät / Fan-Coil: Wandgerät mit Ausblaslamellen.
const gRaumgeraet: Glyph = () => (
  <>
    <rect x="7" y="13" width="26" height="10" rx="3" />
    <path d="M11 17 h18" />
    <path d="M13 26 q2 3 0 6 M20 26 q2 3 0 6 M27 26 q2 3 0 6" />
  </>
);

// GLT-Aufschaltpunkt: Schaltschrank mit Bus-Anbindung.
const gGltAufschaltpunkt: Glyph = () => (
  <>
    <rect x="10" y="8" width="20" height="24" rx="2" />
    <path d="M14 14 h12 M14 19 h12" />
    <circle cx="16" cy="26" r="1.6" />
    <circle cx="22" cy="26" r="1.6" />
    <path d="M20 32 v4 M12 36 h16" />
  </>
);


export const SYMBOL_GLYPHS: Record<string, Glyph> = {
  // Oberflächentechnik / Metallverarbeitung
  entfettungsbad: gEntfettungsbad,
  beizbad: gBeizbad,
  passivierbad: gPassivierbad,
  spuelbad: gSpuelbad,
  haftwassertrockner: gTrockner,
  pulverkabine: gPulverkabine,
  pulverzyklon: gPulverzyklon,
  einbrennofen: gEinbrennofen,
  haengebahn: gHaengebahn,
  rollformlinie: gRollformlinie,
  schaeumkopf: gSchaeumkopf,
  druckluftbehaelter: gDruckluftbehaelter,
  drucklufttrockner: gDrucklufttrockner,
  neutralisationsbecken: gNeutralisation,
  // Behälter / Becken — Biogas
  fermenter: gFermenter,
  nachgaerer: gFermenter,
  gaerrestlager: gBehaelter,
  gasspeicher: gGasspeicher,
  gashaube: gGasspeicher,
  gasspeicher_klaergas: gGasspeicher,
  gaskuehler_kondensat: gKondensat,
  // Behälter / Becken — Abwasser
  zulauf: gZulauf,
  ablauf: gAblauf,
  sandfang: gSandfang,
  vorklaerung: gRundklaerer,
  nachklaerung: gRundklaerer,
  belebungsbecken: gBelebung,
  faulturm: gFaulturm,
  eindicker: gEindicker,
  // Maschinen / Aktoren
  feststoffeintrag: gFeststoffeintrag,
  entschwefelung: gKolonne,
  gasaufbereitung: gKolonne,
  bhkw: gBhkw,
  gasfackel: gFackel,
  ruehrwerk: gRuehrwerk,
  ruehrwerk_a: gRuehrwerk,
  separator: gSeparator,
  waermetauscher: gWaermetauscher,
  verdichter_gas: gVerdichter,
  verdichter: gVerdichter,
  geblaese: gGeblaese,
  geblaese_a: gGeblaese,
  rechen: gRechen,
  pumpwerk: gPumpe,
  kreiselpumpe: gPumpe,
  exzenterschneckenpumpe: gSchneckenpumpe,
  dosierpumpe: gDosierpumpe,
  faellmitteldosierung: gFaellmittel,
  entwaesserung: gEntwaesserung,
  schieber: gSchieber,
  stellventil: gStellventil,
  klappe: gKlappe,
  // Sonderverfahren moderner Kläranlagen
  speicherbecken: gSpeicherbecken,
  biop_becken: gBiopBecken,
  hydrolyse_thermisch: gHydrolyse,
  map_faellung: gMapFaellung,
  biofilter: gBiofilter,
  // Thermische Abfallbehandlung (MHKW)
  muellbunker: gMuellbunker,
  muellkran: gMuellkran,
  rostfeuerung: gRostfeuerung,
  dampfkessel: gDampfkessel,
  dampfturbine: gDampfturbine,
  scr_denox: gScrDenox,
  rauchgaswaesche: gRauchgaswaesche,
  gewebefilter: gGewebefilter,
  schlackebunker: gSchlackebunker,
  kamin: gKamin,
  // Brauerei
  schrotmuehle: gSchrotmuehle,
  maischebottich: gMaischebottich,
  laeuterbottich: gLaeuterbottich,
  wuerzepfanne: gWuerzepfanne,
  whirlpool: gWhirlpool,
  wuerzekuehler: gWuerzekuehler,
  gaertank: gGaertank,
  hefegabe: gHefegabe,
  filtration_bier: gFiltrationBier,
  drucktank: gDrucktank,
  cip_station: gCipStation,
  kaeltemaschine: gKaeltemaschine,
  // Molkerei
  rohmilchtank: gRohmilchtank,
  pasteur: gPasteur,
  homogenisator: gHomogenisator,
  eindampfer: gEindampfer,
  spruehtrockner: gSpruehtrockner,
  // Bäder / Thermen
  schwimmbecken: gSchwimmbecken,
  schwallwasserbehaelter: gSchwallwasserbehaelter,
  filter_mehrschicht: gFilterMehrschicht,
  flockungsdosierung: gFlockungsdosierung,
  chlorung: gChlorung,
  ph_korrektur: gPhKorrektur,
  uv_desinfektion: gUvDesinfektion,
  thermalwasser_quelle: gThermalwasserQuelle,
  // Fernwärme / Wärmenetz
  waermespeicher: gWaermespeicher,
  heizkessel: gHeizkessel,
  waermepumpe: gWaermepumpe,
  waermeuebergabe: gWaermeuebergabe,
  // Wasserwerk
  brunnen: gBrunnen,
  belueftung_entsaeuerung: gBelueftungEntsaeuerung,
  aktivkohlefilter: gAktivkohlefilter,
  reinwasserbehaelter: gReinwasserbehaelter,
  // Wärmenetz-Hydraulik + Erzeuger
  rohrleitungsabschnitt: gRohrleitungsabschnitt,
  netzpumpe: gNetzpumpe,
  druckhaltung: gDruckhaltung,
  verteiler_sammler: gVerteilerSammler,
  geothermie_dublette: gGeothermieDublette,
  solarthermie_feld: gSolarthermieFeld,
  elektrodenkessel: gElektrodenkessel,
  abwaermequelle: gAbwaermequelle,
  // Quartier / Anergienetz
  gebaeude_anschluss: gGebaeudeAnschluss,
  waermepumpe_dezentral: gWaermepumpeDezentral,
  erdsondenfeld: gErdsondenfeld,
  erdwaermekollektor: gErdwaermekollektor,
  pv_feld: gPvFeld,
  batteriespeicher: gBatteriespeicher,
  netzanschlusspunkt: gNetzanschlusspunkt,
  freikuehler: gFreikuehler,
  gebaeudekuehlung: gGebaeudekuehlung,
  // Druckluft
  schraubenkompressor: gSchraubenkompressor,
  adsorptionstrockner: gAdsorptionstrockner,
  filterlinie: gFilterlinie,
  kondensatableiter: gKondensatableiter,
  druckluft_verteilnetz: gDruckluftVerteilnetz,
  // Industriekälte
  verfluessiger: gVerfluessiger,
  rueckkuehlwerk: gRueckkuehlwerk,
  eisspeicher: gEisspeicher,
  kuehlzelle: gKuehlzelle,
  verdichtersatz_kaelte: gVerdichtersatzKaelte,
  // Gebäudetechnik HLK / GLT
  rlt_anlage: gRltAnlage,
  heizkreis: gHeizkreis,
  raumgeraet: gRaumgeraet,
  glt_aufschaltpunkt: gGltAufschaltpunkt,
};

// Kategorie-Fallback: greift, wenn die Symbol-ID im Register fehlt.
const CATEGORY_GLYPHS: Record<string, Glyph> = {
  sensor: gSensor,
  aktor_sollwert: gSensor,
  maschine: gMaschine,
  behaelter: gBehaelter,
};

// Löst Symbol-ID (+ Kategorie) zum passenden Glyph auf: erst exakte ID, dann Kategorie, zuletzt der
// Behälter-Grundform als letzter Anker. Damit ist die Auflösung total (immer ein Glyph).
export function glyphFor(symbolId: string | undefined, category: string | undefined): Glyph {
  if (symbolId && SYMBOL_GLYPHS[symbolId]) return SYMBOL_GLYPHS[symbolId];
  if (category && CATEGORY_GLYPHS[category]) return CATEGORY_GLYPHS[category];
  return gBehaelter;
}

// Fertig gerahmtes Icon zum Einbetten: rendert das aufgelöste Glyph in einem 40×40-SVG mit den
// Standard-Strichattributen. `className` steuert Größe und Farbe (Tailwind), `title` liefert über
// `aria-label` einen zugänglichen Namen (bewusst kein <title>-Element, damit die sichtbare
// Beschriftung im DOM nicht doppelt auftaucht). `data-glyph` trägt die Symbol-ID für Tests/Debugging.
export function PlantSymbolIcon({
  symbolId,
  category,
  className,
  title,
}: {
  symbolId?: string;
  category?: string;
  className?: string;
  title?: string;
}): JSX.Element {
  const Draw = glyphFor(symbolId, category);
  return (
    <Svg
      className={className}
      role="img"
      aria-label={title}
      data-glyph={symbolId ?? category ?? 'behaelter'}
    >
      <Draw />
    </Svg>
  );
}
